<!-- Cookie banner component with consent management -->

<template>
  <div v-if="showBanner" class="cookie-banner">
    <!-- Overlay -->
    <div class="cookie-banner-overlay" @click="closeIfOverlay"></div>

    <!-- Banner container -->
    <div class="cookie-banner-container">
      <!-- Basic banner (visible when not in settings) -->
      <div v-if="!showSettings" class="cookie-banner-content">
        <div class="cookie-banner-header">
          <h3>🍪 Управление cookies</h3>
          <button
            class="cookie-close-btn"
            @click="closeBanner"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <p class="cookie-banner-text">
          Мы используем cookies для работы сайта, аналитики и улучшения
          пользовательского опыта.
        </p>

        <div class="cookie-banner-actions">
          <button class="btn btn-primary" @click="acceptAll">
            Принять все
          </button>
          <button class="btn btn-secondary" @click="rejectAll">
            Только необходимые
          </button>
          <button class="btn btn-outline" @click="openSettings">
            Настроить
          </button>
        </div>

        <p class="cookie-banner-footer">
          <router-link to="/privacy" class="link"
            >Политика конфиденциальности</router-link
          >
          &nbsp;•&nbsp;
          <router-link to="/cookies" class="link">Политика cookies</router-link>
        </p>
      </div>

      <!-- Settings modal (visible when in settings) -->
      <div v-else class="cookie-settings">
        <div class="cookie-settings-header">
          <h3>Настройки cookies</h3>
          <button
            class="cookie-close-btn"
            @click="closeSettings"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <!-- Category toggles -->
        <div class="cookie-categories">
          <!-- Necessary (always on, disabled) -->
          <div class="cookie-category">
            <div class="category-info">
              <h4>Необходимые</h4>
              <p>Требуются для работы сайта (обязательны)</p>
            </div>
            <input
              type="checkbox"
              :checked="true"
              disabled
              class="category-toggle"
            />
          </div>

          <!-- Analytics -->
          <div class="cookie-category">
            <div class="category-info">
              <h4>Аналитические</h4>
              <p>Помогают нам улучшать сайт (Яндекс.Метрика)</p>
            </div>
            <input
              v-model="categories.analytics"
              type="checkbox"
              class="category-toggle"
            />
          </div>

          <!-- Marketing -->
          <div class="cookie-category">
            <div class="category-info">
              <h4>Маркетинговые</h4>
              <p>Для рекламы и переналадки (пиксели соцсетей)</p>
            </div>
            <input
              v-model="categories.marketing"
              type="checkbox"
              class="category-toggle"
            />
          </div>

          <!-- Functional -->
          <div class="cookie-category">
            <div class="category-info">
              <h4>Функциональные</h4>
              <p>Сохранение ваших предпочтений (язык, тема)</p>
            </div>
            <input
              v-model="categories.functional"
              type="checkbox"
              class="category-toggle"
            />
          </div>
        </div>

        <!-- Action buttons -->
        <div class="cookie-settings-actions">
          <button class="btn btn-secondary" @click="closeSettings">
            Отмена
          </button>
          <button class="btn btn-primary" @click="saveSettings">
            Сохранить настройки
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCookieConsent } from '@/composables/useCookieConsent';

const {
  showBanner,
  showSettings,
  categories,
  acceptAll,
  rejectAll,
  saveConsent,
  openSettings,
  closeSettings,
} = useCookieConsent();

const closeBanner = () => {
  // Reject if closing without choosing (default necessary only)
  saveConsent('reject', {
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });
};

const saveSettings = async () => {
  await saveConsent('custom', { ...categories });
};

const closeIfOverlay = (e: MouseEvent) => {
  // Only close if clicking on overlay itself, not content
  if (e.target === e.currentTarget) {
    closeSettings();
  }
};
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.cookie-banner-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: none;
}

.cookie-banner-overlay.active {
  display: block;
}

.cookie-banner-container {
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 24px;
  max-width: 600px;
  margin: 0 auto;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .cookie-banner-container {
    padding: 16px;
  }
}

.cookie-banner-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.cookie-banner-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.cookie-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.cookie-close-btn:hover {
  background-color: #f3f4f6;
}

.cookie-banner-text {
  margin: 0 0 20px 0;
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
}

.cookie-banner-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.btn {
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-primary {
  background-color: #2563eb;
  color: white;
}

.btn-primary:hover {
  background-color: #1d4ed8;
}

.btn-secondary {
  background-color: #e5e7eb;
  color: #1f2937;
}

.btn-secondary:hover {
  background-color: #d1d5db;
}

.btn-outline {
  background-color: transparent;
  color: #2563eb;
  border: 1px solid #2563eb;
}

.btn-outline:hover {
  background-color: #eff6ff;
}

.cookie-banner-footer {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
}

.link {
  color: #2563eb;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

/* Settings styles */
.cookie-settings {
  /* Same as banner content */
}

.cookie-categories {
  margin: 24px 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.cookie-category {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  gap: 16px;
}

.cookie-category:last-child {
  border-bottom: none;
}

.category-info h4 {
  margin: 0 0 4px 0;
  font-size: 15px;
  font-weight: 600;
}

.category-info p {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
}

.category-toggle {
  width: 48px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

.category-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cookie-settings-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
}

.cookie-settings-actions .btn {
  min-width: 120px;
}
</style>
